import {
  type OutletResource,
  OutletsController_list_v1,
  type UpiDeclarationResource,
  UpiDeclarationsController_list_v1,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '@repo/ui/components/table';
import { IconCircleCheck, IconPlus } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useApiClient } from '@/api/api-client';
import { collectPages } from '@/api/collect-pages';
import { isForbidden } from '@/api/failure';
import { systemClock } from '@/lib/clock';
import { formatInstant } from '@/lib/instant';
import { formatPaise } from '@/lib/money';

import { DayPicker } from '../back-office/DayPicker';
import { Pager } from '../back-office/Pager';
import { type PageState, ROWS_PER_PAGE } from '../back-office/pagination';
import {
  EmptyState,
  LoadFailed,
  Screen,
  ScreenFact,
  ScreenTable,
  TableNotice,
  TableSkeleton,
} from '../back-office/Screen';
import { defaultPeriod, periodProblem, type ReportingPeriod } from '../reporting/reporting-period';
import { formatBusinessDate, fromCalendarDate, toCalendarDate } from '../tickets/business-date';
import { DeclareUpiDialog } from './DeclareUpiDialog';

/**
 * The UPI each outlet took on a trading day, as somebody declared it (#258):
 * `GET /v1/upi-declarations`, owner and manager.
 *
 * Part 1 of the daily collection sheet. A declaration is a figure a person
 * reads off the UPI app or the bank statement — it records no sale and no
 * ledger entry, and changes nothing Day close publishes.
 *
 * **Each row is the figure that stands.** The route answers the current
 * declaration of every outlet-day that has one; a corrected figure is replaced
 * here by its correction, and the row says it is one. An outlet-day with no
 * declaration is absent, not zero.
 *
 * **Paged, not read whole**: one row per outlet per day grows with every day
 * the park trades, which is the case `docs/ui-conventions.md` gives `Pager`
 * for. The period is required by the route, so the screen opens on the month
 * ending today, as the other Reporting reads do.
 */

type ListState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly declarations: readonly UpiDeclarationResource[] }
  | { readonly kind: 'failed'; readonly error: unknown };

/** The outlet filter's "not filtering" value. Not `''`, which Base UI clears to. */
const EVERY_OUTLET = 'every-outlet';

const COLUMNS = 5;

export function UpiDeclarations() {
  const client = useApiClient();

  const [period, setPeriod] = useState<ReportingPeriod>(() => defaultPeriod(systemClock));
  const [outletId, setOutletId] = useState<string>(EVERY_OUTLET);

  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<PageState>({ offset: 0, limit: ROWS_PER_PAGE, total: 0 });
  const [state, setState] = useState<ListState>({ kind: 'loading' });

  const [outlets, setOutlets] = useState<readonly OutletResource[]>([]);

  // `null` while closed; `{ correcting: null }` while declaring a new figure.
  const [opened, setOpened] = useState<{ correcting: UpiDeclarationResource | null } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((count) => count + 1), []);

  const problem = periodProblem(period, systemClock);

  useEffect(() => {
    const controller = new AbortController();
    let live = true;

    // Read whole — bounded by the park. Retired included, so a row from an
    // outlet since retired is still named.
    collectPages<OutletResource>((offset, limit) =>
      client.request(OutletsController_list_v1, {
        query: { offset, limit, includeRetired: true },
        signal: controller.signal,
      }),
    )
      .then((all) => {
        if (live) setOutlets(all);
      })
      .catch(() => {
        // The filter, the outlet column and the dialog's picker are the
        // casualties; the list itself still loads.
        if (live) setOutlets([]);
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client]);

  useEffect(() => {
    // A half-filled or refused period is not a request; the note under the
    // pickers says so.
    if (period.from === '' || period.to === '' || problem !== null) return;

    const controller = new AbortController();
    let live = true;

    setState({ kind: 'loading' });

    client
      .request(UpiDeclarationsController_list_v1, {
        query: {
          from: period.from,
          to: period.to,
          outletId: outletId === EVERY_OUTLET ? undefined : outletId,
          offset,
          limit: ROWS_PER_PAGE,
        },
        signal: controller.signal,
      })
      .then((answer) => {
        if (!live) return;
        setState({ kind: 'loaded', declarations: answer.items });
        setPage({ offset: answer.offset, limit: answer.limit, total: answer.total });
      })
      .catch((error: unknown) => {
        if (live && !(error instanceof DOMException && error.name === 'AbortError')) {
          setState({ kind: 'failed', error });
        }
      });

    return () => {
      live = false;
      controller.abort();
    };
  }, [client, period.from, period.to, outletId, offset, problem, reloads]);

  /** A filter change is a new first page. */
  const changeFilter = (apply: () => void) => {
    apply();
    setOffset(0);
  };

  const outletsById = useMemo(
    () => new Map(outlets.map((outlet) => [outlet.id, outlet])),
    [outlets],
  );

  const outletItems = useMemo(
    () => [
      { label: 'Every outlet', value: EVERY_OUTLET },
      ...outlets.map((outlet) => ({ label: outlet.name, value: outlet.id })),
    ],
    [outlets],
  );

  const forbidden = state.kind === 'failed' && isForbidden(state.error);

  return (
    <Screen
      title="UPI takings"
      description="The UPI each outlet received on a trading day, as declared from the UPI app or the bank statement. A figure is never edited — a correction replaces it, and both are kept."
      action={
        <Button onClick={() => setOpened({ correcting: null })}>
          <IconPlus data-icon="inline-start" />
          Declare UPI
        </Button>
      }
      aside={
        <>
          <div className="space-y-2">
            <Label htmlFor="upi-from" className="text-sm text-muted-foreground">
              From
            </Label>
            <DayPicker
              id="upi-from"
              value={period.from}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              onValueChange={(next) => {
                changeFilter(() => setPeriod((held) => ({ ...held, from: next })));
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="upi-to" className="text-sm text-muted-foreground">
              To
            </Label>
            <DayPicker
              id="upi-to"
              value={period.to}
              emptyLabel="Choose a day"
              format={formatBusinessDate}
              fromDate={fromCalendarDate}
              toDate={toCalendarDate}
              onValueChange={(next) => {
                changeFilter(() => setPeriod((held) => ({ ...held, to: next })));
              }}
            />
          </div>

          {problem !== null && (
            <p role="status" className="text-sm text-destructive">
              {problem}
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="upi-outlet" className="text-sm text-muted-foreground">
              Outlet
            </Label>
            <Select
              value={outletId}
              items={outletItems}
              onValueChange={(next) => {
                changeFilter(() => setOutletId(next ?? EVERY_OUTLET));
              }}
            >
              <SelectTrigger
                id="upi-outlet"
                aria-label="Outlet filter"
                disabled={outlets.length === 0}
                className="h-10 w-full bg-card"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {outletItems.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          {state.kind === 'loaded' && (
            <ScreenFact label="Declared">
              <span className="font-mono numeric">{page.total}</span>{' '}
              {page.total === 1 ? 'outlet-day' : 'outlet-days'}
              <br />
              <span className="text-muted-foreground">
                A day with no figure is absent, not zero.
              </span>
            </ScreenFact>
          )}
        </>
      }
    >
      {notice !== null && (
        <Alert role="status">
          <IconCircleCheck />
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      <ScreenTable label="UPI declarations">
        <TableHeader>
          <TableRow>
            <TableHead className="w-40">Trading day</TableHead>
            <TableHead className="w-44">Outlet</TableHead>
            <TableHead className="w-36 text-right">UPI received</TableHead>
            <TableHead>Declared</TableHead>
            <TableHead className="w-28">
              <span className="sr-only">Correct</span>
            </TableHead>
          </TableRow>
        </TableHeader>

        {state.kind === 'loading' && <TableSkeleton columns={COLUMNS} />}

        {state.kind === 'failed' && (
          <TableNotice columns={COLUMNS}>
            {forbidden ? (
              <EmptyState
                title="This account cannot read UPI takings"
                description="Declaring and reading UPI is the owner's and the manager's. If that is what you are, sign out and back in — the role this browser is holding may be out of date."
              />
            ) : (
              <LoadFailed error={state.error} onRetry={reload} />
            )}
          </TableNotice>
        )}

        {state.kind === 'loaded' &&
          (state.declarations.length === 0 ? (
            <TableNotice columns={COLUMNS}>
              <EmptyState
                title="No UPI declared in this period"
                description="Declare UPI records what an outlet received on one day. Widen the period, or try every outlet, if you expected figures here."
              />
            </TableNotice>
          ) : (
            <TableBody>
              {state.declarations.map((declaration) => (
                <DeclarationRow
                  key={declaration.id}
                  declaration={declaration}
                  outlet={outletsById.get(declaration.outletId)}
                  onCorrect={() => setOpened({ correcting: declaration })}
                />
              ))}
            </TableBody>
          ))}
      </ScreenTable>

      {state.kind === 'loaded' && (
        <Pager state={page} label="UPI declarations" onOffsetChange={setOffset} />
      )}

      {opened !== null && (
        <DeclareUpiDialog
          outlets={outlets}
          correcting={opened.correcting}
          onClose={() => setOpened(null)}
          onStale={reload}
          onDeclared={(declaration) => {
            setOpened(null);
            setNotice(
              `${formatPaise(declaration.amountPaise)} ${declaration.correctsDeclarationId === null ? 'declared' : 'recorded as the correction'} for ${outletsById.get(declaration.outletId)?.name ?? 'the outlet'} on ${formatBusinessDate(declaration.businessDate)}.`,
            );
            reload();
          }}
        />
      )}
    </Screen>
  );
}

function DeclarationRow({
  declaration,
  outlet,
  onCorrect,
}: {
  declaration: UpiDeclarationResource;
  outlet: OutletResource | undefined;
  onCorrect: () => void;
}) {
  const day = formatBusinessDate(declaration.businessDate);

  return (
    <TableRow>
      <TableCell className="numeric">{day}</TableCell>

      <TableCell>
        {outlet === undefined ? (
          <span className="text-muted-foreground">An outlet not in reach</span>
        ) : (
          <>
            <span className="font-mono numeric">{outlet.code}</span>
            <br />
            <span className="text-xs text-muted-foreground">{outlet.name}</span>
          </>
        )}
      </TableCell>

      <TableCell className="text-right font-mono numeric">
        {formatPaise(declaration.amountPaise)}
      </TableCell>

      <TableCell className="whitespace-normal">
        {formatInstant(declaration.recordedAt)}
        {declaration.correctsDeclarationId !== null && (
          <>
            <br />
            <span className="text-xs text-muted-foreground">Corrects an earlier figure.</span>
          </>
        )}
      </TableCell>

      <TableCell>
        <Button
          variant="outline"
          size="sm"
          aria-label={`Correct the UPI for ${outlet?.name ?? 'this outlet'} on ${day}`}
          onClick={onCorrect}
        >
          Correct
        </Button>
      </TableCell>
    </TableRow>
  );
}
